import type { CustomFieldViewModel } from '../../custom-fields/models/custom-field.model';

/** GUID sent by API for native card field rows in workflow-fields list. */
export const NATIVE_CARD_FIELD_PLACEHOLDER_ID = '00000000-0000-0000-0000-000000000000';

/** Prefix for select value when `sourceKind === 'NativeCardField'` (not sent to API). */
export const NATIVE_CARD_FIELD_SELECT_PREFIX = 'native:';

/** Select value meaning triggerConfigJson `{}` (wildcard / legacy). */
export const CARD_FIELD_UPDATED_LEGACY_ANY_VALUE = '__legacy_any__';

export type AutomationWorkflowFieldSourceKind = 'WorkflowField' | 'NativeCardField';

export interface AutomationTriggerFieldOption extends CustomFieldViewModel {
  sourceKind: AutomationWorkflowFieldSourceKind;
  nativeCardFieldKey?: string | null;
  /** Value bound to the trigger field select; maps to `triggerConfigJson`. */
  triggerSelectValue: string;
}

export function isAutomationTriggerFieldOption(
  f: CustomFieldViewModel
): f is AutomationTriggerFieldOption {
  return 'triggerSelectValue' in f && typeof (f as AutomationTriggerFieldOption).triggerSelectValue === 'string';
}

/** Conditions (CardField) only support workflow custom fields, not native card keys. */
export function isWorkflowFieldForConditions(
  f: CustomFieldViewModel & { sourceKind?: AutomationWorkflowFieldSourceKind }
): boolean {
  return f.sourceKind !== 'NativeCardField';
}

export function buildCardFieldUpdatedTriggerConfig(selectValue: string): Record<string, unknown> | null {
  const token = selectValue.trim();
  if (!token || token === CARD_FIELD_UPDATED_LEGACY_ANY_VALUE) {
    return {};
  }
  if (token.startsWith(NATIVE_CARD_FIELD_SELECT_PREFIX)) {
    const key = token.slice(NATIVE_CARD_FIELD_SELECT_PREFIX.length).trim();
    return key ? { nativeCardFieldKey: key } : null;
  }
  return { workflowFieldId: token };
}

export function cardFieldUpdatedSelectValueFromTriggerConfig(
  config: Record<string, unknown> | null | undefined
): string {
  if (!config || typeof config !== 'object') {
    return CARD_FIELD_UPDATED_LEGACY_ANY_VALUE;
  }
  const wf =
    typeof config['workflowFieldId'] === 'string' ? config['workflowFieldId'].trim() : '';
  const nk =
    typeof config['nativeCardFieldKey'] === 'string' ? config['nativeCardFieldKey'].trim() : '';
  if (wf && nk) {
    return CARD_FIELD_UPDATED_LEGACY_ANY_VALUE;
  }
  if (nk) {
    return `${NATIVE_CARD_FIELD_SELECT_PREFIX}${nk}`;
  }
  if (wf) {
    return wf;
  }
  return CARD_FIELD_UPDATED_LEGACY_ANY_VALUE;
}
